import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Minus, Trash2, Award } from 'lucide-react';
import { toast } from 'sonner';
import { startRazorpayCheckout } from '@/lib/razorpay';
import { startCashfreeCheckout, preloadCashfree } from '@/lib/cashfree';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Self-contained reward claim flow — used ONLY on the Business page (claiming a
// reward is not allowed in the Company Store). Pick products up to the reward BV
// cap and pay the flat reward price + GST + delivery.
export const RewardClaim = ({ reward, open, onClose, onClaimed }) => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [quote, setQuote] = useState(null);
  const [info, setInfo] = useState({ full_name: '', phone: '', address: '' });
  const [provider, setProvider] = useState('razorpay');
  const [paying, setPaying] = useState(false);

  useEffect(() => { preloadCashfree(); }, []);

  useEffect(() => {
    if (!open) return;
    setCart([]); setQuote(null);
    (async () => {
      try { const res = await api.get('/store/products'); setProducts(res.data?.products || []); }
      catch { toast.error('Could not load products'); }
    })();
  }, [open]);

  const cap = Number(reward?.bv_cap || 25);
  const totalBv = quote?.total_bv || 0;
  const overCap = totalBv > cap + 1e-6;

  const addToCart = (p) => {
    setCart(prev => {
      const ex = prev.find(i => i.product_id === p.product_id);
      if (ex) return prev.map(i => i.product_id === p.product_id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { product_id: p.product_id, title: p.title, price: p.offer_price, image: p.images?.[0], bv: p.bv, qty: 1 }];
    });
  };
  const setQty = (id, d) => setCart(prev => prev.map(i => i.product_id === id ? { ...i, qty: Math.max(1, i.qty + d) } : i));
  const removeItem = (id) => setCart(prev => prev.filter(i => i.product_id !== id));

  const fetchQuote = useCallback(async () => {
    if (cart.length === 0) { setQuote(null); return; }
    try {
      const items = cart.map(i => ({ product_id: i.product_id, qty: i.qty }));
      const res = await api.post('/store/quote', { items });
      setQuote(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Quote failed'); }
  }, [cart]);
  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const delivery = reward?.kind === 'sponsor' ? (reward?.delivery || 0) : (quote?.delivery || 0);
  const payable = Math.round((reward?.price || 0) + (reward?.gst || 0) + delivery);

  const claim = async () => {
    if (cart.length === 0) { toast.error('Add at least one product'); return; }
    if (overCap) { toast.error(`Reward is limited to ${cap} BV — remove some items`); return; }
    if (!info.full_name || !info.phone || !info.address) { toast.error('Fill name, phone and delivery address'); return; }
    setPaying(true);
    try {
      const items = cart.map(i => ({ product_id: i.product_id, qty: i.qty }));
      const extra = {
        reward: true,
        reward_type: reward.kind,
        sponsor_reward_id: reward.kind === 'sponsor' ? reward.reward_id : undefined,
        weekly_reward_id: reward.kind === 'weekly' ? reward.reward_id : undefined,
      };
      if (provider === 'cashfree') {
        await startCashfreeCheckout({
          createPath: '/store/checkout',
          orderPayload: { items, ...info, ...extra },
          returnPath: '/business',
        });
        return;
      }
      await startRazorpayCheckout({
        createPath: '/store/checkout',
        verifyPath: '/store/verify',
        orderPayload: { items, ...info, ...extra },
        phone: info.phone,
        onSuccess: () => { toast.success('Reward claimed! 🎁'); onClaimed?.(); onClose?.(); },
      });
    } catch (e) {
      const d = e?.response?.data?.detail || e.message || 'Payment failed';
      toast.error(d === 'reward_on_hold' ? 'This reward is still on hold' : d);
    } finally { setPaying(false); }
  };

  if (!reward) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="reward-claim-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Award className="h-5 w-5 text-[var(--bz-orange)]" />Claim your {reward.title}</DialogTitle>
        </DialogHeader>

        <div className={`rounded-lg p-2.5 text-xs font-semibold ${overCap ? 'bg-red-50 text-red-600' : 'bg-[var(--bz-green-tint)] text-[#1E3A8A]'}`} data-testid="reward-claim-banner">
          Add up to {cap} BV of products · used {totalBv}/{cap} BV · you pay a flat {money(reward.price)} + {reward.gst_pct}% GST + delivery.
          {overCap && <span className="block">Over the {cap} BV limit — remove some items.</span>}
        </div>

        {/* Product picker */}
        <div className="max-h-56 overflow-y-auto grid grid-cols-1 gap-2" data-testid="reward-product-list">
          {products.map(p => (
            <div key={p.product_id} className="flex items-center gap-2 rounded-lg border border-[var(--bz-line)] p-2" data-testid={`reward-product-${p.product_id}`}>
              {p.images?.[0] && <img src={p.images[0]} alt="" className="h-10 w-10 rounded object-cover" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{p.title}</div>
                <div className="text-[11px] text-[var(--bz-muted)]">{p.bv || 0} BV · {money(p.offer_price)}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => addToCart(p)} className="rounded-full h-7 px-2" data-testid={`reward-add-${p.product_id}`}><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
          {products.length === 0 && <div className="text-sm text-[var(--bz-muted)] py-2">No products available.</div>}
        </div>

        {/* Cart */}
        {cart.length > 0 && (
          <div className="space-y-1.5" data-testid="reward-cart">
            {cart.map(i => (
              <div key={i.product_id} className="flex items-center gap-2 text-sm">
                <span className="flex-1 truncate">{i.title} <span className="text-[11px] text-[var(--bz-muted)]">({i.bv || 0} BV)</span></span>
                <button onClick={() => setQty(i.product_id, -1)} className="p-1" data-testid={`reward-qty-minus-${i.product_id}`}><Minus className="h-3.5 w-3.5" /></button>
                <span className="w-5 text-center">{i.qty}</span>
                <button onClick={() => setQty(i.product_id, 1)} className="p-1" data-testid={`reward-qty-plus-${i.product_id}`}><Plus className="h-3.5 w-3.5" /></button>
                <button onClick={() => removeItem(i.product_id)} className="p-1 text-red-500" data-testid={`reward-remove-${i.product_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}

        {/* Delivery details */}
        <div className="space-y-2">
          <Input placeholder="Full name" value={info.full_name} onChange={e => setInfo({ ...info, full_name: e.target.value })} data-testid="reward-name-input" />
          <Input placeholder="Phone" value={info.phone} onChange={e => setInfo({ ...info, phone: e.target.value })} data-testid="reward-phone-input" />
          <Input placeholder="Delivery address" value={info.address} onChange={e => setInfo({ ...info, address: e.target.value })} data-testid="reward-address-input" />
        </div>

        {/* Totals */}
        <div className="rounded-lg bg-[var(--bz-soft)] p-3 text-sm space-y-1">
          <div className="flex justify-between"><span>Reward price</span><span>{money(reward.price)}</span></div>
          <div className="flex justify-between text-[12px] text-[var(--bz-muted)]"><span>GST ({reward.gst_pct}%)</span><span>{money(reward.gst)}</span></div>
          <div className="flex justify-between text-[12px] text-[var(--bz-muted)]"><span>Delivery</span><span>{money(delivery)}</span></div>
          <div className="flex justify-between font-black border-t border-[var(--bz-line)] pt-1"><span>You pay</span><span data-testid="reward-payable">{money(payable)}</span></div>
        </div>

        {/* Provider */}
        <div className="flex gap-2">
          {['razorpay', 'cashfree'].map(pv => (
            <button key={pv} onClick={() => setProvider(pv)} data-testid={`reward-provider-${pv}`}
              className={`flex-1 rounded-full py-1.5 text-xs font-semibold capitalize ${provider === pv ? 'bg-[#1D4ED8] text-white' : 'bg-[var(--bz-soft)]'}`}>{pv}</button>
          ))}
        </div>

        <Button onClick={claim} disabled={paying || cart.length === 0 || overCap} className="w-full rounded-full bg-[var(--bz-orange)] hover:bg-[#e65100] text-white" data-testid="reward-claim-pay-btn">
          {paying ? 'Processing…' : `Claim & pay ${money(payable)}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
